import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { UploadCloud, X } from "lucide-react";
import { createProduct, updateProduct, uploadMedia } from "@/lib/api";
import { ICON_MAP } from "@/lib/iconMap";
import { MediaTile } from "@/components/MediaTile";

const CATEGORY_LABELS = {
  gold: "Gold",
  silver: "Silver",
  diamond: "Diamond",
  others: "Others",
  offers: "Offers",
  festival: "Festival Offers",
  banner: "Homepage Banner",
  arrivals_video: "New Arrivals Video",
};

function getInitialForm(product) {
  return {
    name: product?.name || "",
    description: product?.description || "",
    tag: product?.tag || "",
    purity: product?.purity || "",
    media_type: product?.media_type || "image",
    media_url: product?.media_url || "",
    gallery_images: product?.gallery_images || [],
    gallery_videos: product?.gallery_videos || [],
    icon: product?.icon || "Sparkles",
    coming_soon: product?.coming_soon || false,
    status_text: product?.status_text || "",
    display_order: product?.display_order ?? 0,
    is_active: product?.is_active ?? true,
  };
}

const MAX_GALLERY_IMAGES = 4;
const MAX_GALLERY_VIDEOS = 2;

function buildPayload(category, form, isEdit) {
  const payload = {
    category,
    name: form.name.trim(),
    description: form.description,
    tag: form.tag,
    media_type: form.media_url ? form.media_type : "image",
    media_url: form.media_url || null,
    gallery_images: form.gallery_images,
    gallery_videos: form.gallery_videos,
    icon: form.icon,
    coming_soon: Boolean(form.coming_soon),
    status_text: form.status_text,
    display_order: Number(form.display_order) || 0,
    is_active: Boolean(form.is_active),
  };
  if (form.purity) {
    payload.purity = form.purity;
  } else if (isEdit) {
    payload.clear_purity = true;
  }
  return payload;
}

export const ProductFormDialog = ({ open, onOpenChange, category, product, onSaved }) => {
  const isEdit = Boolean(product?.id);
  const [form, setForm] = useState(() => getInitialForm(product));
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(getInitialForm(product));
  }, [product, open]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const res = await uploadMedia(file, setProgress);
      setForm((f) => ({ ...f, media_url: res.url, media_type: res.media_type }));
      toast.success("Media uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleGalleryImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (form.gallery_images.length >= MAX_GALLERY_IMAGES) {
      toast.error(`Maximum ${MAX_GALLERY_IMAGES} gallery photos allowed`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const res = await uploadMedia(file, setProgress);
      setForm((f) => ({ ...f, gallery_images: [...f.gallery_images, res.url] }));
      toast.success("Photo added to gallery");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleGalleryVideoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (form.gallery_videos.length >= MAX_GALLERY_VIDEOS) {
      toast.error(`Maximum ${MAX_GALLERY_VIDEOS} gallery videos allowed`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const res = await uploadMedia(file, setProgress);
      setForm((f) => ({ ...f, gallery_videos: [...f.gallery_videos, res.url] }));
      toast.success("Video added to gallery");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeGalleryImage = (url) =>
    setForm((f) => ({ ...f, gallery_images: f.gallery_images.filter((u) => u !== url) }));

  const removeGalleryVideo = (url) =>
    setForm((f) => ({ ...f, gallery_videos: f.gallery_videos.filter((u) => u !== url) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(category, form, isEdit);
      if (isEdit) {
        await updateProduct(product.id, payload);
        toast.success("Updated successfully");
      } else {
        await createProduct(payload);
        toast.success("Added successfully");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto bg-card border-border">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-foreground">
            {isEdit ? "Edit" : "Add New"} {CATEGORY_LABELS[category]} Item
          </DialogTitle>
          <DialogDescription>Fill in the details below. Changes go live on the website immediately after saving.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2" data-testid="product-form">
          {/* Media upload */}
          <div>
            <Label>Photo / Video</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="relative h-24 w-24 rounded-xl overflow-hidden bg-muted border border-border flex items-center justify-center shrink-0">
                {form.media_url ? (
                  <MediaTile
                    src={form.media_url}
                    mediaType={form.media_type}
                    alt="preview"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <UploadCloud className="h-6 w-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                  className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary/15 file:text-primary file:px-3 file:py-2 file:text-xs file:font-medium file:cursor-pointer hover:file:bg-primary/25"
                  data-testid="product-media-input"
                />
                <p className="mt-1.5 text-[11px] text-muted-foreground">Images or videos, up to 30MB.</p>
                {uploading && <Progress value={progress} className="mt-2 h-1.5" data-testid="product-upload-progress" />}
                {form.media_url && !uploading && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, media_url: "", media_type: "image" }))}
                    className="mt-2 text-xs text-destructive hover:underline flex items-center gap-1"
                    data-testid="product-remove-media-btn"
                  >
                    <X className="h-3 w-3" /> Remove media
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Gallery photos (up to 4) */}
          <div>
            <Label>Gallery Photos ({form.gallery_images.length}/{MAX_GALLERY_IMAGES})</Label>
            <div className="mt-2 flex flex-wrap gap-3">
              {form.gallery_images.map((url) => (
                <div key={url} className="relative h-20 w-20 rounded-lg overflow-hidden bg-muted border border-border">
                  <MediaTile src={url} mediaType="image" alt="gallery" className="absolute inset-0 w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeGalleryImage(url)}
                    className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white hover:bg-destructive"
                    data-testid={`remove-gallery-image-${url}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {form.gallery_images.length < MAX_GALLERY_IMAGES && (
                <label className="h-20 w-20 rounded-lg border border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors">
                  <UploadCloud className="h-5 w-5 text-muted-foreground" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleGalleryImageChange}
                    className="hidden"
                    data-testid="gallery-image-input"
                  />
                </label>
              )}
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">Add extra photos shown in a swipeable gallery on the site.</p>
          </div>

          {/* Gallery videos (up to 2) */}
          <div>
            <Label>Gallery Videos ({form.gallery_videos.length}/{MAX_GALLERY_VIDEOS})</Label>
            <div className="mt-2 flex flex-wrap gap-3">
              {form.gallery_videos.map((url) => (
                <div key={url} className="relative h-20 w-20 rounded-lg overflow-hidden bg-muted border border-border">
                  <MediaTile src={url} mediaType="video" alt="gallery video" className="absolute inset-0 w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeGalleryVideo(url)}
                    className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white hover:bg-destructive"
                    data-testid={`remove-gallery-video-${url}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {form.gallery_videos.length < MAX_GALLERY_VIDEOS && (
                <label className="h-20 w-20 rounded-lg border border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors">
                  <UploadCloud className="h-5 w-5 text-muted-foreground" />
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleGalleryVideoChange}
                    className="hidden"
                    data-testid="gallery-video-input"
                  />
                </label>
              )}
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">Add extra short videos shown alongside the gallery photos.</p>
          </div>

          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              required
              value={form.name}
              onChange={(e) => set("name")(e.target.value)}
              data-testid="product-name-input"
            />
          </div>

          {(category === "gold" || category === "diamond") && (
            <>
              <div>
                <Label htmlFor="tag">Subtitle / Tag</Label>
                <Input
                  id="tag"
                  value={form.tag}
                  onChange={(e) => set("tag")(e.target.value)}
                  placeholder="e.g. Evil Eye Charm Ring"
                  data-testid="product-tag-input"
                />
              </div>
              <div>
                <Label>Purity</Label>
                <Select value={form.purity || "none"} onValueChange={(v) => set("purity")(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="product-purity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set (ask on WhatsApp)</SelectItem>
                    <SelectItem value="18K">18K</SelectItem>
                    <SelectItem value="22K">22K</SelectItem>
                    <SelectItem value="24K">24K</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {(category === "others" || category === "offers") && (
            <>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => set("description")(e.target.value)}
                  data-testid="product-description-input"
                />
              </div>
              <div>
                <Label>Fallback Icon (shown only if no photo/video uploaded)</Label>
                <Select value={form.icon} onValueChange={set("icon")}>
                  <SelectTrigger data-testid="product-icon-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {Object.keys(ICON_MAP).map((name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <Label htmlFor="coming_soon" className="cursor-pointer">
                  Mark as &quot;Coming Soon&quot;
                </Label>
                <Switch
                  id="coming_soon"
                  checked={form.coming_soon}
                  onCheckedChange={set("coming_soon")}
                  data-testid="product-coming-soon-switch"
                />
              </div>
            </>
          )}

          {category === "festival" && (
            <div>
              <Label htmlFor="status_text">Status Text</Label>
              <Input
                id="status_text"
                value={form.status_text}
                onChange={(e) => set("status_text")(e.target.value)}
                placeholder="e.g. Rakhi Offer Coming Soon"
                data-testid="product-status-input"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="display_order">Display Order</Label>
              <Input
                id="display_order"
                type="number"
                value={form.display_order}
                onChange={(e) => set("display_order")(e.target.value)}
                data-testid="product-order-input"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3 mt-6">
              <Label htmlFor="is_active" className="cursor-pointer text-sm">
                Visible
              </Label>
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={set("is_active")}
                data-testid="product-active-switch"
              />
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="premium" disabled={saving || uploading} data-testid="product-save-btn">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Item"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
